<template>
    <div class="doc-ocr">
        <button
            type="button"
            class="doc-ocr__open-btn"
            @click="open"
        >
            {{ buttonText }} <i class="far fa-address-card ml-3" style="font-size: 1.3rem"></i>
        </button>

        <div
            v-if="isOpen"
            class="doc-ocr__overlay"
            @click.self="close"
        >
            <div class="doc-ocr__modal">
                <div class="doc-ocr__header">
                    <div>
                        <h3 class="doc-ocr__title">Распознавание документа</h3>
                        <div class="doc-ocr__subtitle">
                            Загрузите скан паспорта / водительского удостоверения
                        </div>
                    </div>

                    <button
                        type="button"
                        class="doc-ocr__close"
                        @click="close"
                    >
                        ×
                    </button>
                </div>

                <div class="doc-ocr__body">
                    <div
                        v-if="error"
                        class="doc-ocr__alert doc-ocr__alert--error"
                    >
                        {{ error }}
                    </div>

                    <div
                        v-if="warnings.length"
                        class="doc-ocr__alert doc-ocr__alert--warning"
                    >
                        <div
                            v-for="warning in warnings"
                            :key="warning"
                        >
                            {{ warning }}
                        </div>
                    </div>

                    <div v-if="step === 'select' || step === 'uploading'">
                        <input
                            ref="fileInput"
                            type="file"
                            class="doc-ocr__file-input"
                            :accept="accept"
                            @change="onFileChange"
                        >

                        <div
                            class="doc-ocr__dropzone"
                            @click="chooseFile"
                        >
                            <div class="doc-ocr__dropzone-title">
                                {{ file ? file.name : 'Выберите файл с компьютера' }}
                            </div>

                            <div class="doc-ocr__dropzone-subtitle">
                                JPG, PNG, WEBP
                            </div>
                        </div>

                        <div
                            v-if="previewUrl"
                            class="doc-ocr__preview-wrap"
                        >
                            <img
                                :src="previewUrl"
                                class="doc-ocr__preview"
                                alt="Предпросмотр документа"
                            >
                        </div>

                        <div class="doc-ocr__actions">
                            <button
                                type="button"
                                class="doc-ocr__btn doc-ocr__btn--secondary"
                                @click="close"
                                :disabled="isBusy"
                            >
                                Отмена
                            </button>

                            <button
                                type="button"
                                class="doc-ocr__btn doc-ocr__btn--primary"
                                @click="recognize"
                                :disabled="!file || isBusy"
                            >
                                {{ isRecognizing ? 'Распознаём...' : 'Загрузить и распознать' }}
                            </button>
                        </div>
                    </div>

                    <div v-if="step === 'recognized' || step === 'applying' || step === 'applied'">
                        <div class="doc-ocr__recognized-head">
                            <div>
                                <div class="doc-ocr__doc-type">
                                    {{ documentTitle }}
                                </div>

                                <div class="doc-ocr__hint">
                                    Проверьте данные и выберите поля, которые нужно применить к анкете.
                                </div>
                            </div>

                            <button
                                type="button"
                                class="doc-ocr__link-btn"
                                @click="reset"
                                :disabled="isBusy"
                            >
                                Загрузить другой файл
                            </button>
                        </div>

                        <div
                            v-if="rows.length"
                            class="doc-ocr__table-wrap"
                        >
                            <table class="doc-ocr__table">
                                <thead>
                                <tr>
                                    <th>Применить</th>
                                    <th>Поле</th>
                                    <th>Сейчас в анкете</th>
                                    <th>Распознано</th>
                                </tr>
                                </thead>

                                <tbody>
                                <tr
                                    v-for="row in rows"
                                    :key="row.key"
                                    :class="{ 'doc-ocr__row--changed': row.changed }"
                                >
                                    <td>
                                        <input
                                            type="checkbox"
                                            v-model="selectedFields[row.key]"
                                            :disabled="!row.recognized || isBusy"
                                        >
                                    </td>

                                    <td>
                                        {{ row.label }}
                                    </td>

                                    <td class="doc-ocr__muted">
                                        {{ row.current || '—' }}
                                    </td>

                                    <td>
                                        <input
                                            v-model="recognizedFields[row.key]"
                                            type="text"
                                            class="doc-ocr__field-input"
                                            :disabled="isBusy"
                                        >
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>

                        <div
                            v-else
                            class="doc-ocr__empty"
                        >
                            Не удалось извлечь поля из документа.
                        </div>

                        <div
                            v-if="step === 'applied'"
                            class="doc-ocr__alert doc-ocr__alert--success"
                        >
                            Данные применены к анкете.
                        </div>

                        <div class="doc-ocr__actions">
                            <button
                                type="button"
                                class="doc-ocr__btn doc-ocr__btn--secondary"
                                @click="close"
                                :disabled="isBusy"
                            >
                                Закрыть
                            </button>

                            <button
                                type="button"
                                class="doc-ocr__btn doc-ocr__btn--primary"
                                @click="apply"
                                :disabled="!hasSelectedFields || isBusy"
                            >
                                {{ isApplying ? 'Применяем...' : 'Применить выбранные поля' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'DocumentOcrModal',

    props: {
        recognizeUrl: {
            type: String,
            required: false,
            default: '/api/request_profiles/doc-ocr',
        },

        applyUrl: {
            type: String,
            required: false,
            default: null,
        },

        currentFields: {
            type: Object,
            default: function () {
                return {};
            },
        },

        buttonText: {
            type: String,
            default: 'Заполнить из документа',
        },

        accept: {
            type: String,
            default: 'image/jpeg,image/png,image/webp',
        },
    },

    data: function () {
        return {
            isOpen: false,
            step: 'select',

            file: null,
            previewUrl: null,

            isRecognizing: false,
            isApplying: false,

            error: null,
            warnings: [],

            ocrRequestId: null,
            documentType: null,
            documentTitle: null,

            recognizedFields: {},
            selectedFields: {},
        };
    },

    computed: {
        isBusy: function () {
            return this.isRecognizing || this.isApplying;
        },

        rows: function () {
            var rows = [];
            var fields = this.recognizedFields || {};

            for (var key in fields) {
                if (!Object.prototype.hasOwnProperty.call(fields, key)) {
                    continue;
                }

                var recognized = fields[key];
                var current = this.currentFields[key] || null;

                rows.push({
                    key: key,
                    label: this.getFieldLabel(key),
                    current: current,
                    recognized: recognized,
                    changed: String(current || '') !== String(recognized || ''),
                });
            }

            return rows;
        },

        hasSelectedFields: function () {
            var selected = this.getSelectedPayload();

            return Object.keys(selected).length > 0;
        },
    },

    methods: {
        open: function () {
            this.isOpen = true;
        },

        close: function () {
            if (this.isBusy) {
                return;
            }

            this.isOpen = false;
            this.reset();
        },

        reset: function () {
            if (this.isBusy) {
                return;
            }

            this.step = 'select';
            this.file = null;
            this.error = null;
            this.warnings = [];
            this.ocrRequestId = null;
            this.documentType = null;
            this.documentTitle = null;
            this.recognizedFields = {};
            this.selectedFields = {};

            if (this.previewUrl) {
                URL.revokeObjectURL(this.previewUrl);
            }

            this.previewUrl = null;

            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
        },

        chooseFile: function () {
            if (this.isBusy) {
                return;
            }

            this.$refs.fileInput.click();
        },

        onFileChange: function (event) {
            var files = event.target.files;

            if (!files || !files.length) {
                return;
            }

            this.file = files[0];
            this.error = null;

            if (this.previewUrl) {
                URL.revokeObjectURL(this.previewUrl);
            }

            this.previewUrl = URL.createObjectURL(this.file);
        },

        recognize: function () {
            var self = this;

            if (!this.file || this.isRecognizing) {
                return;
            }

            this.error = null;
            this.warnings = [];
            this.isRecognizing = true;
            this.step = 'uploading';

            var formData = new FormData();

            formData.append('file', this.file);
            formData.append('check_quality', 'true');
            formData.append('img_size', '1500');

            axios.post(this.recognizeUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 180000,
            })
                .then(function (response) {
                    var data = response.data || {};

                    if (data.success === false) {
                        throw new Error(data.error || 'Не удалось распознать документ');
                    }

                    self.ocrRequestId = data.ocr_request_id || data.id || null;
                    self.documentType = data.document_type || self.getNested(data, ['normalized', 'document_type']);
                    self.documentTitle = data.document_title || self.getDocumentTitle(self.documentType);

                    var fields = data.fields || self.getNested(data, ['normalized', 'fields']) || {};
                    var warnings = data.warnings || self.getNested(data, ['normalized', 'warnings']) || [];

                    self.recognizedFields = self.prepareFields(fields);
                    self.warnings = warnings;
                    self.selectedFields = self.makeDefaultSelectedFields(self.recognizedFields);

                    self.step = 'recognized';
                })
                .catch(function (error) {
                    self.step = 'select';
                    self.error = self.extractErrorMessage(error);
                })
                .finally(function () {
                    self.isRecognizing = false;
                });
        },

        apply: function () {
            var self = this;

            if (this.isApplying) {
                return;
            }

            var fields = this.getSelectedPayload();

            if (!Object.keys(fields).length) {
                this.error = 'Выберите хотя бы одно поле для применения.';
                return;
            }

            this.error = null;
            this.isApplying = true;
            this.step = 'applying';

            if (this.applyUrl) {
                axios.post(this.applyUrl, {
                    ocr_request_id: this.ocrRequestId,
                    document_type: this.documentType,
                    fields: fields,
                }, {
                    timeout: 60000,
                })
                    .then(function (response) {
                        self.step = 'applied';

                        self.$emit('applied', {
                            response: response.data,
                            fields: fields,
                            ocr_request_id: self.ocrRequestId,
                            document_type: self.documentType,
                        });
                    })
                    .catch(function (error) {
                        self.step = 'recognized';
                        self.error = self.extractErrorMessage(error);
                    })
                    .finally(function () {
                        self.isApplying = false;
                    });
            } else {
                self.isApplying = false;
                self.step = 'applied';
                self.$emit('applied', fields);
                self.close();
            }
        },

        getSelectedPayload: function () {
            var result = {};

            for (var key in this.selectedFields) {
                if (!Object.prototype.hasOwnProperty.call(this.selectedFields, key)) {
                    continue;
                }

                if (!this.selectedFields[key]) {
                    continue;
                }

                var value = this.recognizedFields[key];

                if (value === null || value === undefined || value === '') {
                    continue;
                }

                result[key] = value;
            }

            return result;
        },

        makeDefaultSelectedFields: function (fields) {
            var result = {};

            for (var key in fields) {
                if (!Object.prototype.hasOwnProperty.call(fields, key)) {
                    continue;
                }

                var value = fields[key];
                var current = this.currentFields[key] || null;

                result[key] = Boolean(
                    value !== null &&
                    value !== undefined &&
                    value !== '' &&
                    String(value) !== String(current || '')
                );
            }

            return result;
        },

        prepareFields: function (fields) {
            var result = {};

            for (var key in fields) {
                if (!Object.prototype.hasOwnProperty.call(fields, key)) {
                    continue;
                }

                var value = fields[key];

                if (Array.isArray(value)) {
                    value = value.join(', ');
                }

                if (value === null || value === undefined) {
                    value = '';
                }

                result[key] = String(value);
            }

            return result;
        },

        getFieldLabel: function (key) {
            var labels = {
                last_name: 'Фамилия',
                first_name: 'Имя',
                middle_name: 'Отчество',
                birthdate: 'Дата рождения',
                birthplace: 'Место рождения',

                passport_series: 'Серия паспорта',
                passport_number: 'Номер паспорта',
                passport_issue_date: 'Дата выдачи паспорта',
                passport_issuing_authority: 'Кем выдан паспорт',
                passport_division_code: 'Код подразделения',

                driving_license_series: 'Серия ВУ',
                driving_license_number: 'Номер ВУ',
                driving_license_division_code: 'Дата выдачи ВУ',
                driving_license_issuing_authority: 'Кем выдано ВУ',
            };

            return labels[key] || key;
        },

        getDocumentTitle: function (documentType) {
            var titles = {
                passport: 'Паспорт РФ',
                driver_license: 'Водительское удостоверение РФ',
                snils: 'СНИЛС',
            };

            return titles[documentType] || 'Документ';
        },

        getNested: function (object, path) {
            var value = object;

            for (var i = 0; i < path.length; i++) {
                if (!value || typeof value !== 'object') {
                    return null;
                }

                value = value[path[i]];
            }

            return value;
        },

        extractErrorMessage: function (error) {
            if (error.response && error.response.data) {
                var data = error.response.data;

                if (data.message) {
                    return data.message;
                }

                if (data.error) {
                    return data.error;
                }

                if (data.detail) {
                    return data.detail;
                }
            }

            if (error.message) {
                return error.message;
            }

            return 'Произошла ошибка при распознавании документа.';
        },
    },
};
</script>

<style scoped>
.doc-ocr__open-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 14px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #abdfc4;
    color: #0f172a;
    font-size: 14px;
    cursor: pointer;
}

.doc-ocr__open-btn:hover {
    background: #9eceb5;
}

.doc-ocr__overlay {
    position: fixed;
    z-index: 9999;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(15, 23, 42, 0.55);
}

.doc-ocr__modal {
    width: 900px;
    max-width: 100%;
    max-height: 90vh;
    overflow: auto;
    border-radius: 10px;
    background: #ffffff;
    box-shadow: 0 20px 60px rgba(15, 23, 42, 0.25);
}

.doc-ocr__header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
}

.doc-ocr__title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
}

.doc-ocr__subtitle {
    margin-top: 4px;
    font-size: 13px;
    color: #64748b;
}

.doc-ocr__close {
    width: 32px;
    height: 32px;
    border: 0;
    background: transparent;
    color: #64748b;
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
}

.doc-ocr__body {
    padding: 24px;
}

.doc-ocr__file-input {
    display: none;
}

.doc-ocr__dropzone {
    padding: 28px;
    border: 2px dashed #cbd5e1;
    border-radius: 10px;
    background: #f8fafc;
    text-align: center;
    cursor: pointer;
}

.doc-ocr__dropzone:hover {
    border-color: #94a3b8;
    background: #f1f5f9;
}

.doc-ocr__dropzone-title {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
}

.doc-ocr__dropzone-subtitle {
    margin-top: 6px;
    font-size: 13px;
    color: #64748b;
}

.doc-ocr__preview-wrap {
    margin-top: 16px;
    text-align: center;
}

.doc-ocr__preview {
    max-width: 100%;
    max-height: 260px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.doc-ocr__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 22px;
}

.doc-ocr__btn {
    padding: 9px 14px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
}

.doc-ocr__btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.doc-ocr__btn--primary {
    border: 1px solid #2563eb;
    background: #2563eb;
    color: #ffffff;
}

.doc-ocr__btn--secondary {
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #0f172a;
}

.doc-ocr__recognized-head {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}

.doc-ocr__doc-type {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
}

.doc-ocr__hint {
    margin-top: 4px;
    font-size: 13px;
    color: #64748b;
}

.doc-ocr__link-btn {
    border: 0;
    background: transparent;
    color: #2563eb;
    cursor: pointer;
}

.doc-ocr__table-wrap {
    overflow-x: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.doc-ocr__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}

.doc-ocr__table th,
.doc-ocr__table td {
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    vertical-align: middle;
}

.doc-ocr__table th {
    background: #f8fafc;
    color: #334155;
    font-weight: 600;
}

.doc-ocr__table tr:last-child td {
    border-bottom: 0;
}

.doc-ocr__row--changed {
    background: #fff7ed;
}

.doc-ocr__muted {
    color: #64748b;
}

.doc-ocr__field-input {
    width: 100%;
    min-width: 180px;
    padding: 7px 8px;
    border: 1px solid #cbd5e1;
    border-radius: 5px;
    font-size: 14px;
}

.doc-ocr__alert {
    margin-bottom: 16px;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 14px;
}

.doc-ocr__alert--error {
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #991b1b;
}

.doc-ocr__alert--warning {
    border: 1px solid #fed7aa;
    background: #fff7ed;
    color: #9a3412;
}

.doc-ocr__alert--success {
    margin-top: 16px;
    border: 1px solid #bbf7d0;
    background: #f0fdf4;
    color: #166534;
}

.doc-ocr__empty {
    padding: 20px;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    color: #64748b;
    text-align: center;
}
</style>
